import React, { useEffect, useRef, useState } from 'react';
import { Card, Table, Badge, Button, Modal, Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { format } from 'date-fns';

const safeFormat = (dateStr, fmt) => {
  try {
    if (!dateStr) return '--';
    return format(new Date(dateStr), fmt);
  } catch {
    return '--';
  }
};

const TASK_STATUS_CONFIG = {
  COMPLETED: { label: 'Hoàn thành', variant: 'success' },
  PENDING: { label: 'Chờ xử lý', variant: 'warning' },
  IN_PROGRESS: { label: 'Đang làm', variant: 'primary' },
  FAILED: { label: 'Thất bại', variant: 'danger' },
  WAITING_PEER_REVIEW: { label: 'Chờ review', variant: 'info' },
  WAITING_ADMIN_DECISION: { label: 'Chờ admin', variant: 'secondary' },
  CANCELLED: { label: 'Đã hủy', variant: 'dark' },
};

const calcDuration = (startedAt, completedAt) => {
  try {
    if (!startedAt || !completedAt) return null;
    const diff = new Date(completedAt) - new Date(startedAt);
    if (diff < 0) return null;
    const mins = Math.round(diff / 60000);
    return mins < 60 ? `${mins}p` : `${Math.floor(mins / 60)}h${String(mins % 60).padStart(2, '0')}p`;
  } catch {
    return null;
  }
};

const TasksTab = ({ tasks = [], taskSummary = {}, onTaskReview, onTaskRedo, focusTaskId }) => {
  const [selectedTask, setSelectedTask] = useState(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewAction, setReviewAction] = useState(null);
  const [reviewNote, setReviewNote] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);
  const [lightboxUrl, setLightboxUrl] = useState(null);

  // #171726 — yêu cầu nhân viên làm lại/chụp lại
  const [redoTask, setRedoTask] = useState(null);
  const [redoReason, setRedoReason] = useState('');
  const [isRedoProcessing, setIsRedoProcessing] = useState(false);

  // #171726 — link sâu (?task=) mở sẵn đúng dòng task và cuộn tới, để quản lý sửa nhanh.
  const rowRefs = useRef({});
  const [highlightTaskId, setHighlightTaskId] = useState(null);

  useEffect(() => {
    if (!focusTaskId || !tasks.some((t) => t.id === focusTaskId)) return;
    setExpandedRow(focusTaskId);
    setHighlightTaskId(focusTaskId);
    const timer = setTimeout(() => {
      rowRefs.current[focusTaskId]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 150);
    return () => clearTimeout(timer);
  }, [focusTaskId, tasks]);

  const handleReviewClick = (task, action) => {
    setSelectedTask(task);
    setReviewAction(action);
    setReviewNote('');
    setShowReviewModal(true);
  };

  const handleRedoClick = (task) => {
    setRedoTask(task);
    setRedoReason('');
  };

  const handleConfirmRedo = async () => {
    if (!redoTask || !onTaskRedo) return;
    if (!redoReason.trim()) return;
    setIsRedoProcessing(true);
    try {
      await onTaskRedo(redoTask.id, redoReason.trim());
      setRedoTask(null);
      setRedoReason('');
    } catch {
      // error handled by parent
    } finally {
      setIsRedoProcessing(false);
    }
  };

  const handleConfirmReview = async () => {
    if (!selectedTask || !reviewAction) return;
    setIsProcessing(true);
    try {
      await onTaskReview(selectedTask.id, reviewAction, reviewNote);
      setShowReviewModal(false);
    } catch {
      // error handled by parent
    } finally {
      setIsProcessing(false);
    }
  };

  const canReview = (status) =>
    status === 'COMPLETED' || status === 'WAITING_PEER_REVIEW';

  const canArbitrate = (status) =>
    status === 'WAITING_ADMIN_DECISION' || status === 'FAILED';

  // #171726 — chỉ yêu cầu làm lại task đã nộp (hoàn thành / chờ review).
  const canRedo = (status) =>
    status === 'COMPLETED' || status === 'WAITING_PEER_REVIEW';

  return (
    <>
      {/* Summary Cards */}
      <div className='row g-3 mb-4'>
        <div className='col-6 col-md-3'>
          <Card className='shadow-sm border-0 h-100'>
            <Card.Body className='d-flex align-items-center gap-3'>
              <div className='rounded bg-success bg-opacity-10 p-2 d-flex align-items-center justify-content-center' style={{ width: 44, height: 44 }}>
                <i className='icofont-check-circled text-success fs-4'></i>
              </div>
              <div>
                <div className='fw-bold fs-4'>{taskSummary.completed || 0}/{taskSummary.total || 0}</div>
                <div className='text-muted small'>Hoàn thành</div>
              </div>
            </Card.Body>
          </Card>
        </div>
        <div className='col-6 col-md-3'>
          <Card className='shadow-sm border-0 h-100'>
            <Card.Body className='d-flex align-items-center gap-3'>
              <div className='rounded bg-info bg-opacity-10 p-2 d-flex align-items-center justify-content-center' style={{ width: 44, height: 44 }}>
                <i className='icofont-eye text-info fs-4'></i>
              </div>
              <div>
                <div className='fw-bold fs-4'>{taskSummary.waiting_review || 0}</div>
                <div className='text-muted small'>Chờ review</div>
              </div>
            </Card.Body>
          </Card>
        </div>
        <div className='col-6 col-md-3'>
          <Card className='shadow-sm border-0 h-100'>
            <Card.Body className='d-flex align-items-center gap-3'>
              <div className='rounded bg-warning bg-opacity-10 p-2 d-flex align-items-center justify-content-center' style={{ width: 44, height: 44 }}>
                <i className='icofont-star text-warning fs-4'></i>
              </div>
              <div>
                <div className='fw-bold fs-4'>{taskSummary.total_points_earned || 0}</div>
                <div className='text-muted small'>Điểm thưởng</div>
              </div>
            </Card.Body>
          </Card>
        </div>
        <div className='col-6 col-md-3'>
          <Card className='shadow-sm border-0 h-100'>
            <Card.Body className='d-flex align-items-center gap-3'>
              <div className='rounded bg-primary bg-opacity-10 p-2 d-flex align-items-center justify-content-center' style={{ width: 44, height: 44 }}>
                <i className='icofont-chart-pie text-primary fs-4'></i>
              </div>
              <div>
                <div className='fw-bold fs-4'>{taskSummary.completion_rate || 0}%</div>
                <div className='text-muted small'>Tỷ lệ hoàn thành</div>
              </div>
            </Card.Body>
          </Card>
        </div>
      </div>

      {/* Task Table */}
      <Card className='shadow-sm border-0'>
        <Card.Body className='p-0'>
          <Table responsive hover className='mb-0 align-middle'>
            <thead className='bg-light'>
              <tr>
                <th className='border-bottom-0 ps-4' style={{ width: 50 }}>STT</th>
                <th className='border-bottom-0'>Tên công việc</th>
                <th className='border-bottom-0' style={{ width: 130 }}>Trạng thái</th>
                <th className='border-bottom-0' style={{ width: 180 }}>Thời gian</th>
                <th className='border-bottom-0' style={{ width: 120 }}>Bằng chứng</th>
                <th className='border-bottom-0 text-end pe-4' style={{ width: 100 }}>Thao tác</th>
              </tr>
            </thead>
            <tbody>
              {tasks.map((task, idx) => {
                const statusCfg = TASK_STATUS_CONFIG[task.status] || { label: task.status, variant: 'secondary' };
                const isExpanded = expandedRow === task.id;
                const duration = calcDuration(task.started_at, task.completed_at);

                return (
                  <React.Fragment key={task.id}>
                    <tr
                      ref={(el) => { rowRefs.current[task.id] = el; }}
                      onClick={() => setExpandedRow(isExpanded ? null : task.id)}
                      style={{
                        cursor: 'pointer',
                        ...(highlightTaskId === task.id
                          ? { boxShadow: 'inset 3px 0 0 0 #0d6efd', background: '#eef5ff' }
                          : {}),
                      }}
                    >
                      <td className='ps-4 text-muted'>{idx + 1}</td>
                      <td>
                        {/* #181825: task Task Center/dọn bàn (task_id = null) mang tên thật ở
                            `ShiftTask.extra.name`; máy chủ đã đọc nhánh đó vào `task_name`.
                            Giữ thêm `task.extra?.name` để màn không vỡ nếu chạy với bản API cũ. */}
                        <div className='fw-medium'>
                          {task.task_name || task.extra?.name || 'Không tên'}
                          {/* #213032 — 2 lượt của mẫu "nghiệm thu 2 lần" cùng tên, phải nêu rõ lượt nào. */}
                          {task.shift_segment_label && (
                            <Badge bg='info' className='ms-2'>{task.shift_segment_label}</Badge>
                          )}
                        </div>
                        {task.checklist_completed && Object.keys(task.checklist_completed).length > 0 && (
                          <div className='small text-muted'>
                            <i className='icofont-list me-1'></i>
                            {Object.values(task.checklist_completed).filter(Boolean).length}/
                            {Object.keys(task.checklist_completed).length} checklist
                          </div>
                        )}
                        {task.category && (
                          <Badge bg='light' text='dark' className='x-small'>{task.category}</Badge>
                        )}
                      </td>
                      <td><Badge bg={statusCfg.variant}>{statusCfg.label}</Badge></td>
                      <td className='small'>
                        <div className='text-muted'>
                          {safeFormat(task.started_at, 'HH:mm')} - {safeFormat(task.completed_at, 'HH:mm')}
                        </div>
                        <div className='d-flex gap-2'>
                          {duration && (
                            <span className='text-primary fw-medium'>{duration}</span>
                          )}
                          {task.estimated_minutes && (
                            <span className='text-muted'>
                              (ước tính {task.estimated_minutes}p)
                            </span>
                          )}
                        </div>
                      </td>
                      <td>
                        {task.evidence_urls?.length > 0 ? (
                          <div className='d-flex gap-1'>
                            {task.evidence_urls.slice(0, 3).map((url, i) => (
                              <div
                                key={i}
                                style={{ cursor: 'pointer' }}
                                onClick={(e) => { e.stopPropagation(); setLightboxUrl(url); }}
                              >
                                <img src={url} alt='' className='rounded border' style={{ width: 36, height: 28, objectFit: 'cover' }} />
                              </div>
                            ))}
                            {task.evidence_urls.length > 3 && (
                              <span className='text-muted small align-self-center'>+{task.evidence_urls.length - 3}</span>
                            )}
                          </div>
                        ) : (
                          <span className='text-muted'>—</span>
                        )}
                      </td>
                      <td className='text-end pe-4'>
                        {canReview(task.status) && (
                          <>
                            <OverlayTrigger overlay={<Tooltip>Duyệt</Tooltip>}>
                              <Button variant='link' className='text-success p-0 me-2' onClick={(e) => { e.stopPropagation(); handleReviewClick(task, 'APPROVE'); }}>
                                <i className='icofont-check-circled fs-5'></i>
                              </Button>
                            </OverlayTrigger>
                            <OverlayTrigger overlay={<Tooltip>Từ chối</Tooltip>}>
                              <Button variant='link' className='text-danger p-0' onClick={(e) => { e.stopPropagation(); handleReviewClick(task, 'REJECT'); }}>
                                <i className='icofont-close-circled fs-5'></i>
                              </Button>
                            </OverlayTrigger>
                          </>
                        )}
                        {canArbitrate(task.status) && (
                          <OverlayTrigger overlay={<Tooltip>Admin phân xử</Tooltip>}>
                            <Button variant='link' className='text-primary p-0' onClick={(e) => { e.stopPropagation(); handleReviewClick(task, 'ADMIN_ARBITRATE'); }}>
                              <i className='icofont-gavel fs-6'></i>
                            </Button>
                          </OverlayTrigger>
                        )}
                        {canRedo(task.status) && onTaskRedo && (
                          <OverlayTrigger overlay={<Tooltip>Yêu cầu chụp lại / làm lại</Tooltip>}>
                            <Button variant='link' className='text-warning p-0 ms-2' onClick={(e) => { e.stopPropagation(); handleRedoClick(task); }}>
                              <i className='icofont-camera fs-6'></i>
                            </Button>
                          </OverlayTrigger>
                        )}
                      </td>
                    </tr>

                    {/* Expanded Detail Row */}
                    {isExpanded && (
                      <tr>
                        <td colSpan='6' className='bg-light px-4 py-3'>
                          <div className='row g-3'>
                            <div className='col-md-6'>
                              <div className='small text-muted mb-1'>Mô tả</div>
                              <div className='small'>{task.task_description || 'Không có mô tả'}</div>
                            </div>
                            <div className='col-md-3'>
                              <div className='small text-muted mb-1'>Người review</div>
                              <div className='small'>{task.reviewed_by?.full_name || '—'}</div>
                              {task.reviewed_at && (
                                <div className='small text-muted'>{safeFormat(task.reviewed_at, 'dd/MM HH:mm')}</div>
                              )}
                              {task.review_result && (
                                <Badge
                                  bg={task.review_result === 'APPROVED' ? 'success' : 'danger'}
                                  className='mt-1'
                                >
                                  {task.review_result}
                                </Badge>
                              )}
                            </div>
                            <div className='col-md-3'>
                              <div className='small text-muted mb-1'>Ghi chú review</div>
                              <div className='small'>{task.review_notes || '—'}</div>
                              {task.admin_review_note && (
                                <div className='small mt-1'>
                                  <Badge bg='info' className='me-1'>Admin</Badge>
                                  {task.admin_review_note}
                                </div>
                              )}
                            </div>
                            {task.redo_reason && (
                              <div className='col-12'>
                                <div className='small text-muted mb-1'>Yêu cầu làm lại</div>
                                <div className='small'>
                                  <Badge bg='warning' text='dark' className='me-1'>Chụp lại</Badge>
                                  {task.redo_reason}
                                </div>
                              </div>
                            )}

                            {/* Checklist Detail */}
                            {task.checklist_items?.length > 0 && (
                              <div className='col-12'>
                                <div className='small text-muted mb-1'>Checklist chi tiết</div>
                                <div className='d-flex flex-wrap gap-2'>
                                  {task.checklist_items.map((item, i) => {
                                    const itemKey = typeof item === 'string' ? item : item.key || item.name || `item_${i}`;
                                    const itemLabel = typeof item === 'string' ? item : item.label || item.name || itemKey;
                                    const isChecked = task.checklist_completed?.[itemKey] === true;
                                    return (
                                      <Badge
                                        key={i}
                                        bg={isChecked ? 'success' : 'light'}
                                        text={isChecked ? 'white' : 'dark'}
                                        className='py-1 px-2'
                                      >
                                        <i className={`icofont-${isChecked ? 'check' : 'close'} me-1`}></i>
                                        {itemLabel}
                                      </Badge>
                                    );
                                  })}
                                </div>
                              </div>
                            )}

                            {/* Evidence Gallery */}
                            {task.evidence_urls?.length > 0 && (
                              <div className='col-12'>
                                <div className='small text-muted mb-1'>Bằng chứng hoàn thành</div>
                                <div className='d-flex gap-2 flex-wrap'>
                                  {task.evidence_urls.map((url, i) => (
                                    <div
                                      key={i}
                                      style={{ cursor: 'pointer' }}
                                      onClick={() => setLightboxUrl(url)}
                                    >
                                      <img src={url} alt='' className='rounded border' style={{ width: 100, height: 75, objectFit: 'cover' }} />
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            {task.review_evidence_urls?.length > 0 && (
                              <div className='col-12'>
                                <div className='small text-muted mb-1'>Bằng chứng review</div>
                                <div className='d-flex gap-2 flex-wrap'>
                                  {task.review_evidence_urls.map((url, i) => (
                                    <div
                                      key={i}
                                      style={{ cursor: 'pointer' }}
                                      onClick={() => setLightboxUrl(url)}
                                    >
                                      <img src={url} alt='' className='rounded border' style={{ width: 100, height: 75, objectFit: 'cover' }} />
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {tasks.length === 0 && (
                <tr>
                  <td colSpan='6' className='text-center py-4 text-muted'>
                    Không có công việc nào trong ca này
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {/* Review Modal */}
      <Modal show={showReviewModal} onHide={() => setShowReviewModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {reviewAction === 'ADMIN_ARBITRATE' ? 'Admin phân xử' : 'Đánh giá công việc'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className='fw-bold mb-2'>{selectedTask?.task_name || selectedTask?.extra?.name || 'Không tên'}</p>
          <Form.Group className='mb-3'>
            <Form.Label>Ghi chú đánh giá</Form.Label>
            <Form.Control
              as='textarea'
              rows={3}
              value={reviewNote}
              onChange={(e) => setReviewNote(e.target.value)}
              placeholder='Nhập ghi chú...'
            />
          </Form.Group>
          {reviewAction === 'ADMIN_ARBITRATE' && (
            <div className='alert alert-info small'>
              <i className='icofont-info-circle me-1'></i>
              Quyết định của bạn sẽ là cuối cùng và cập nhật điểm số tương ứng.
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => setShowReviewModal(false)}>Hủy</Button>
          <Button
            variant={reviewAction === 'REJECT' ? 'danger' : 'primary'}
            onClick={handleConfirmReview}
            disabled={isProcessing}
          >
            {isProcessing ? 'Đang xử lý...' : 'Xác nhận'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Yêu cầu chụp lại / làm lại (#171726) */}
      <Modal show={!!redoTask} onHide={() => setRedoTask(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Yêu cầu chụp lại / làm lại</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className='fw-bold mb-2'>{redoTask?.task_name || redoTask?.extra?.name || 'Không tên'}</p>
          <p className='text-muted small mb-3'>
            Task sẽ quay lại trạng thái cần làm và nhân viên nhận được thông báo kèm lý do dưới đây để chụp/nộp lại.
          </p>
          <Form.Group>
            <Form.Label>Lý do (bắt buộc)</Form.Label>
            <Form.Control
              as='textarea'
              rows={3}
              value={redoReason}
              onChange={(e) => setRedoReason(e.target.value)}
              placeholder='Vd: Ảnh chưa đúng mẫu, chụp lại góc khu vực...'
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => setRedoTask(null)}>Hủy</Button>
          <Button
            variant='warning'
            onClick={handleConfirmRedo}
            disabled={isRedoProcessing || !redoReason.trim()}
          >
            {isRedoProcessing ? 'Đang xử lý...' : 'Yêu cầu làm lại'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Lightbox Modal */}
      <Modal show={!!lightboxUrl} onHide={() => setLightboxUrl(null)} centered size='lg'>
        <Modal.Header closeButton>
          <Modal.Title className='small'>Bằng chứng</Modal.Title>
        </Modal.Header>
        <Modal.Body className='text-center p-2'>
          {lightboxUrl && (
            <img
              src={lightboxUrl}
              alt='Evidence'
              style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
            />
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default TasksTab;
